// /app/frontend/src/components/admin/fabric/BrandsTreePanel.jsx
// Left panel component for brands and collections tree - Compatible with main component state
// Supports drag-and-drop reordering

import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { tierLabels } from '../../../lib/fabricLabels';
import { InlineName } from './InlineName';
import {
  Plus, Pencil, Trash2, ChevronDown, ChevronRight,
  Building2, FolderOpen, LayoutTemplate, Palette, GripVertical
} from 'lucide-react';

const BrandsTreePanel = ({
  brands,
  collectionsByBrand,
  expandedBrands,
  setExpandedBrands,
  selectedBrand,
  setSelectedBrand,
  selectedCollection,
  setSelectedCollection,
  setSelectedMaterial,
  onEditBrand,
  onDeleteBrand,
  onAddCollection,
  onOpenTemplateModal,
  onReorderBrands,
  onReorderCollections,
  onRename
}) => {
  const [draggingBrand, setDraggingBrand] = useState(null);
  const [draggingCollection, setDraggingCollection] = useState(null);
  
  // Handle brand drag start
  const handleBrandDragStart = (e, brand) => {
    setDraggingBrand(brand.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'brand', id: brand.id }));
  };
  
  // Handle brand drag end
  const handleBrandDragEnd = () => {
    setDraggingBrand(null);
  };
  
  // Handle brand drag over
  const handleBrandDragOver = (e, targetBrand) => {
    e.preventDefault();
    if (draggingBrand && draggingBrand !== targetBrand.id) {
      // Reorder brands
      const dragIndex = brands.findIndex(b => b.id === draggingBrand);
      const hoverIndex = brands.findIndex(b => b.id === targetBrand.id);
      
      if (dragIndex !== hoverIndex && onReorderBrands) {
        const newOrder = [...brands];
        const [removed] = newOrder.splice(dragIndex, 1);
        newOrder.splice(hoverIndex, 0, removed);
        onReorderBrands(newOrder);
      }
    }
  };
  
  // Handle collection drag start
  const handleCollectionDragStart = (e, coll, brandId) => {
    setDraggingCollection({ id: coll.id, brandId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'collection', id: coll.id, brandId }));
    e.stopPropagation();
  };
  
  // Handle collection drag end
  const handleCollectionDragEnd = () => {
    setDraggingCollection(null);
  };
  
  // Handle collection drag over
  const handleCollectionDragOver = (e, targetColl, brandId) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingCollection && draggingCollection.brandId === brandId && draggingCollection.id !== targetColl.id) {
      const collections = collectionsByBrand[brandId] || [];
      const dragIndex = collections.findIndex(c => c.id === draggingCollection.id);
      const hoverIndex = collections.findIndex(c => c.id === targetColl.id);
      
      if (dragIndex !== hoverIndex && onReorderCollections) {
        const newOrder = [...collections];
        const [removed] = newOrder.splice(dragIndex, 1);
        newOrder.splice(hoverIndex, 0, removed);
        onReorderCollections(brandId, newOrder);
      }
    }
  };

  return (
    <div className="col-span-4 bg-white rounded-xl border border-neutral-200 overflow-hidden" data-testid="brands-tree-panel">
      <div className="p-4 border-b border-neutral-200 bg-neutral-50">
        <h3 className="font-semibold text-neutral-800 flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Brands & Collections
        </h3>
        <p className="text-xs text-neutral-500 mt-1">Drag to reorder</p>
      </div>
      <div className="max-h-[600px] overflow-y-auto">
        {brands.length === 0 ? (
          <div className="p-8 text-center text-neutral-500">
            <Palette className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No brands yet</p>
            <p className="text-sm mt-1">Add a brand or import Coulisse data</p>
          </div>
        ) : (
          brands.map(brand => (
            <div 
              key={brand.id} 
              className={`border-b border-neutral-100 last:border-0 ${draggingBrand === brand.id ? 'opacity-50' : ''}`}
              draggable
              onDragStart={(e) => handleBrandDragStart(e, brand)}
              onDragEnd={handleBrandDragEnd}
              onDragOver={(e) => handleBrandDragOver(e, brand)}
            >
              {/* Brand Row */}
              <div
                className={`flex items-center gap-2 p-3 hover:bg-neutral-50 cursor-pointer ${
                  selectedBrand?.id === brand.id ? 'bg-amber-50' : ''
                }`}
                onClick={() => {
                  setExpandedBrands(prev => ({ ...prev, [brand.id]: !prev[brand.id] }));
                  setSelectedBrand(brand);
                }}
              >
                <GripVertical className="w-4 h-4 text-neutral-300 cursor-grab active:cursor-grabbing" />
                {expandedBrands[brand.id] ? (
                  <ChevronDown className="w-4 h-4 text-neutral-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-neutral-400" />
                )}
                <Building2 className="w-4 h-4 text-amber-600" />
                <InlineName
                  value={brand.name}
                  onSave={(name) => onRename('brand', { brand_id: brand.id }, name)}
                  className="font-medium text-neutral-800 flex-1"
                  inputClassName="text-sm font-medium"
                  testId={`brand-name-${brand.id}`}
                />
                <span className="text-xs text-neutral-400">
                  {collectionsByBrand[brand.id]?.length || 0} {tierLabels(brand.name).tier2Plural.toLowerCase()}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditBrand(brand);
                  }}
                  className="p-1 hover:bg-neutral-200 rounded"
                >
                  <Pencil className="w-3 h-3 text-neutral-500" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteBrand(brand);
                  }}
                  className="p-1 hover:bg-red-100 rounded"
                >
                  <Trash2 className="w-3 h-3 text-red-500" />
                </button>
              </div>
              
              {/* Collections under brand */}
              <AnimatePresence>
                {expandedBrands[brand.id] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-neutral-50"
                  >
                    {collectionsByBrand[brand.id]?.map(coll => (
                      <div
                        key={coll.id}
                        className={`flex items-center gap-2 pl-8 pr-3 py-2 hover:bg-neutral-100 cursor-pointer ${
                          selectedCollection?.id === coll.id ? 'bg-amber-100' : ''
                        } ${draggingCollection?.id === coll.id ? 'opacity-50' : ''}`}
                        onClick={() => {
                          setSelectedCollection(coll);
                          setSelectedMaterial(null);
                        }}
                        draggable
                        onDragStart={(e) => handleCollectionDragStart(e, coll, brand.id)}
                        onDragEnd={handleCollectionDragEnd}
                        onDragOver={(e) => handleCollectionDragOver(e, coll, brand.id)}
                      >
                        <GripVertical className="w-3 h-3 text-neutral-300 cursor-grab active:cursor-grabbing" />
                        <FolderOpen className="w-4 h-4 text-neutral-400" />
                        <InlineName
                          value={coll.name}
                          onSave={(name) => onRename('collection', { collection_id: coll.id }, name)}
                          className="text-sm text-neutral-700 flex-1"
                          inputClassName="text-sm"
                          testId={`collection-name-${coll.id}`}
                        />
                        <span className="text-xs text-neutral-400">
                          {coll.materials?.length > 0 
                            ? `${coll.materials.length} ${tierLabels(brand.name).tier3Plural.toLowerCase()}`
                            : `${coll.colors?.length || 0} colors`}
                        </span>
                      </div>
                    ))}
                    <button
                      onClick={() => onAddCollection(brand.id)}
                      className="flex items-center gap-2 pl-11 pr-3 py-2 text-sm text-amber-600 hover:bg-amber-50 w-full"
                    >
                      <Plus className="w-4 h-4" />
                      Add {tierLabels(brand.name).tier2}
                    </button>
                    <button
                      onClick={() => onOpenTemplateModal(brand)}
                      className="flex items-center gap-2 pl-11 pr-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50 w-full"
                    >
                      <LayoutTemplate className="w-4 h-4" />
                      From Template
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BrandsTreePanel;
